import React, { useState, useEffect } from "react";
import { useNavigate, useBlocker } from "react-router-dom";

import { useForm, FormProvider, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";

import { useUnsavedWarning } from "../useUnsavedWarning";

import {
  Box,
  Paper,
  Button,
  TextField,
  Typography,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  Divider,
  Alert,
  Grid,
  InputAdornment,
  IconButton,
  Tooltip,
  Stepper,
  Step,
  StepLabel,
  AlertTitle,
} from "@mui/material";
import { useMediaQuery, useTheme } from "@mui/material";

import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

import { getVerificationStatus } from "../../api/users";
import { createAuction } from "../../api/auctions";

import MUIAlert from "../../components/UI/MUIAlert";
import LocationPicker from "../../components/UI/LocationPicker";
import FilePicker from "../../components/UI/FilePicker";
import TreePicker from "../../components/UI/TreePicker";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

export let propertyTypeLabels;

export let regionLabels;

export let forestStatusLabels;

export let measurementStatusLabels;

export let fellingTypeLabels;

export let accessAgreementLabels;

export let accessConditionsLabels;

export let fellingOwnerLabels;

export let contractTypeLabels;

export let signatureTypeLabels;

export let treeTypeLabels;

const twoWeeksFromNow = new Date();
twoWeeksFromNow.setDate(twoWeeksFromNow.getDate() + 14);

const createDefaultEndTime = () => {
  const date = new Date();
  date.setDate(date.getDate() + 20);
  date.setHours(10, 0, 0, 0);
  return date;
};

const CreateAuction = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [blockNavigation, setBlockNavigation] = React.useState(true);
  useUnsavedWarning(blockNavigation);

  let steps = [
    t("forms.step_auction"),
    t("forms.step_location"),
    t("forms.step_property"),
    t("forms.step_contract"),
    t("forms.step_extra"),
    t("forms.step_attachments"),
    t("forms.step_overview"),
  ];

  propertyTypeLabels = {
    property: t("general.property"),
    felling: t("general.felling"),
    timber: t("general.timber"),
    wood_chips: t("general.wood_chips"),
  };

  regionLabels = {
    riga: t("forms.region_riga"),
    kurzeme: t("forms.region_kurzeme"),
    vidzeme: t("forms.region_vidzeme"),
    zemgale: t("forms.region_zemgale"),
    latgale: t("forms.region_latgale"),
  };

  forestStatusLabels = {
    none: t("forms.forest_status_none"),
    unknown: t("forms.forest_status_unknown"),
    partially: t("forms.forest_status_partially"),
    fully: t("forms.forest_status_fully"),
  };

  measurementStatusLabels = {
    no: t("forms.measurement_status_no"),
    yes: t("forms.measurement_status_yes"),
  };

  fellingTypeLabels = {
    clear: t("forms.felling_type_clear"),
    selection: t("forms.felling_type_selection"),
    maintenance: t("forms.felling_type_maintenance"),
    random: t("forms.felling_type_random"),
  };

  accessAgreementLabels = {
    none: t("forms.access_agreement_none"),
    agreed: t("forms.access_agreement_agreed"),
    verbal: t("forms.access_agreement_verbal"),
    not_agreed: t("forms.access_agreement_not_agreed"),
  };

  accessConditionsLabels = {
    none: t("forms.access_conditions_none"),
    bad: t("forms.access_conditions_bad"),
    okay: t("forms.access_conditions_okay"),
    good: t("forms.access_conditions_good"),
  };

  fellingOwnerLabels = {
    same: t("forms.felling_owner_same"),
    different: t("forms.felling_owner_different"),
  };

  contractTypeLabels = {
    standard: t("forms.contract_type_standard"),
    attachment: t("forms.contract_type_attachment"),
  };

  signatureTypeLabels = {
    digital: t("forms.signature_type_digital"),
    physical: t("forms.signature_type_physical"),
  };

  treeTypeLabels = [
    { label: t("forms.tree_type_unknown"), code: "unknown" },
    { label: t("forms.tree_type_priede"), code: "priede" },
    { label: t("forms.tree_type_egle"), code: "egle" },
    { label: t("forms.tree_type_berzs"), code: "berzs" },
    { label: t("forms.tree_type_apse"), code: "apse" },
    { label: t("forms.tree_type_malksnis"), code: "malksnis" },
    { label: t("forms.tree_type_balksnis"), code: "balksnis" },
    { label: t("forms.tree_type_ozols"), code: "ozols" },
    { label: t("forms.tree_type_osis"), code: "osis" },
    { label: t("forms.tree_type_liepa"), code: "liepa" },
    { label: t("forms.tree_type_bligzna"), code: "bligzna" },
    { label: t("forms.tree_type_klava"), code: "klava" },
    { label: t("forms.tree_type_sauskegle"), code: "sauskegle" },
    { label: t("forms.tree_type_sauskpriede"), code: "sauskpriede" },
    { label: t("forms.tree_type_other"), code: "other" },
  ];

  const step1Schema = yup.object().shape({
    propertyType: yup
      .string()
      .required(t("errors.property_type_required"))
      .oneOf(
        ["property", "felling", "timber", "wood_chips"],
        t("errors.property_type_invalid")
      ),
    startingPrice: yup
      .number()
      .typeError(t("errors.number_invalid"))
      .required(t("errors.start_price_required"))
      .positive(t("errors.number_positive")),
    endTime: yup
      .date()
      .required(t("errors.end_time_required"))
      .min(twoWeeksFromNow, t("errors.end_time_too_soon")),
  });

  const step2Schema = yup.object().shape({
    region: yup
      .string()
      .required(t("errors.region_required"))
      .oneOf(
        ["riga", "kurzeme", "vidzeme", "latgale", "zemgale"],
        t("errors.invalid")
      ),
    propertyAddress: yup
      .string()
      .required(t("errors.property_address_required")),
    propertyName: yup.string().required(t("errors.property_name_required")),
    cadastre: yup.string().required(t("errors.cadastre_required")),
  });

  const step3SchemaProperty = yup.object().shape({
    propertySize: yup.string().required(t("errors.property_size_required")),
    forestSize: yup.string().required(t("errors.forest_size_required")),
    forestStatus: yup.string().required(t("errors.forest_status_required")),
  });
  const step3SchemaFelling = yup.object().shape({
    harvestableArea: yup
      .string()
      .required(t("errors.harvestable_area_required")),
    liquidFellingArea: yup
      .string()
      .required(t("errors.liquid_felling_area_required")),
    measurementStatus: yup
      .string()
      .required(t("errors.measurement_status_required")),
    fellingType: yup.string().required(t("errors.felling_type_required")),
    accessAgreement: yup
      .string()
      .required(t("errors.access_agreement_required")),
    accessConditions: yup
      .string()
      .required(t("errors.access_conditions_required")),
    fellingOwner: yup.string().required(t("errors.felling_owner_required")),
  });
  const step3SchemaTimber = yup.object().shape({
    timberDescription: yup
      .string()
      .required(t("errors.timber_description_required")),
  });

  const step4Schema = yup.object().shape({
    contractType: yup
      .string()
      .required(t("errors.contract_type_required"))
      .oneOf(["standard", "attachment"], t("errors.invalid")),
    signatureType: yup
      .string()
      .required(t("errors.signature_type_required"))
      .oneOf(["digital", "physical"], t("errors.invalid")),
  });

  const step5Schema = yup.object().shape({
    phone: yup
      .string()
      .transform((value) => value.replace(/\s+/g, ""))
      .matches(/^[0-9]{8,15}$/, t("errors.phone_invalid"))
      .required(t("errors.phone_required")),
    email: yup
      .string()
      .trim()
      .lowercase()
      .email(t("errors.email_invalid"))
      .required(t("errors.email_required")),
  });

  let auctionSchema = step1Schema
    .concat(step2Schema)
    .concat(step4Schema)
    .concat(step5Schema);

  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [formStep, setFormStep] = useState(1);
  const defaultEndTime = createDefaultEndTime();

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success"); // Default severity

  const [userEmail, setUserEmail] = useState(null);
  const [userPhone, setUserPhone] = useState(null);

  const [attachments, setAttachments] = useState([]);

  const {
    register,
    clearErrors,
    trigger,
    control,
    setValue,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(auctionSchema),
    mode: "onChange",
    defaultValues: {
      propertyType: "property",
      email: userEmail,
      phone: userPhone,
      startingPrice: "1000",
      endTime: defaultEndTime,
    },
  });
  const methods = useForm({
    resolver: yupResolver(auctionSchema),
  });

  const propertyType = watch("propertyType");
  const forestStatus = watch("forestStatus");
  const trees = watch("trees");
  const values = watch();

  const handleNextStep = async (currentStep) => {
    clearErrors();

    let fieldsToValidate;
    let valid = false;

    if (currentStep == 1) {
      fieldsToValidate = ["propertyType", "startingPrice", "endTime"];
      valid = await trigger(fieldsToValidate);
    }
    if (currentStep == 2) {
      if (propertyType == "property" || propertyType == "felling") {
        fieldsToValidate = [
          "cadastre",
          "region",
          "propertyAddress",
          "propertyName",
        ];
      } else {
        fieldsToValidate = ["region", "propertyAddress"];
      }
      valid = await trigger(fieldsToValidate);
    }
    if (currentStep == 3) {
      if (propertyType == "property") {
        auctionSchema = auctionSchema.concat(step3SchemaProperty);
        if (forestStatus === "none") {
          fieldsToValidate = ["propertySize", "forestStatus"];
        } else {
          fieldsToValidate = ["propertySize", "forestStatus", "forestSize"];
        }
      } else if (propertyType == "felling") {
        auctionSchema = auctionSchema.concat(step3SchemaFelling);
        fieldsToValidate = [
          "harvestableArea",
          "liquidFellingArea",
          "measurementStatus",
          "fellingType",
          "accessAgreement",
          "accessConditions",
          "fellingOwner",
        ];
      } else {
        fieldsToValidate = ["timberDescription"];
      }
      valid = await trigger(fieldsToValidate);
    }
    if (currentStep == 4) {
      fieldsToValidate = ["contractType", "signatureType"];
      valid = await trigger(fieldsToValidate);
    }
    if (currentStep == 5) {
      fieldsToValidate = ["email", "phone"];
      valid = await trigger(fieldsToValidate);
    }
    if (currentStep == 6) valid = true;

    if (valid) {
      // alert("N");
      setFormStep((prev) => prev + 1);
    }
    window.scrollTo(0, 0);
  };
  const handlePreviousStep = async (currentStep) => {
    clearErrors();

    setFormStep((prev) => prev - 1);
    window.scrollTo(0, 0);
  };

  const NextStepButton = () => {
    return (
      <Button
        variant="contained"
        fullWidth
        color="secondary"
        size="large"
        sx={{
          mt: 2,
          py: 1,
          fontWeight: "bold",
          display: "flex", // Use flexbox for centering
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "50px",
          // py: 1.4,
          // STRIPE SHADOW
          // boxShadow: '0px 0px 0px rgba(0,0,0,0.0)',
          "&:hover": {
            backgroundColor: "secondary.light",
            // boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.08), 0px 1px 3px rgba(0, 0, 0, 0.05)',
          },
        }}
        onClick={() => handleNextStep(formStep)}
      >
        {t("create_auction_page.next_step")}
      </Button>
    );
  };
  const PreviousStepButton = () => {
    return (
      <Button
        variant="contained"
        fullWidth
        color="secondary"
        size="large"
        sx={{
          mt: 2,
          py: 1,
          fontWeight: "bold",
          display: {
            xs: "flex",
            sm: "none",
          },
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "50px",
          backgroundColor: "rgba(226, 226, 226, 1)",
          color: "rgba(58, 58, 58, 1)",
          // py: 1.4,
          // STRIPE SHADOW
          // boxShadow: '0px 0px 0px rgba(0,0,0,0.0)',
          "&:hover": {
            backgroundColor: "rgba(206, 206, 206, 1)",
            // boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.08), 0px 1px 3px rgba(0, 0, 0, 0.05)',
          },
        }}
        onClick={() => handlePreviousStep(formStep)}
      >
        {t("create_auction_page.previous_step")}
      </Button>
    );
  };

  const showAlert = (message, severity = "success") => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
  };

  const handleAlertClose = (event, reason) => {
    if (reason === "clickaway") return;
    setAlertOpen(false);
  };

  const fetchUserStatus = async () => {
    try {
      const userStatus = await getVerificationStatus();
      if (userStatus.email) {
        setUserEmail(userStatus.email);
        setValue("email", userStatus.email);
      }
      if (userStatus.phone) {
        setUserPhone(userStatus.phone);
        setValue("phone", userStatus.phone);
      }
      if (userStatus.verified != true) {
        navigate("/sveicinats");
      }
    } catch (err) {}
  };

  const handleRandomFelling = () => {
    setValue("propertyType", "felling");
    setValue("startingPrice", 1000);
    setValue("propertyName", "Alises");
    setValue("region", "kurzeme");
    setValue("cadastre", "440044");
    setValue("propertyAddress", "Saldus novads, Alūksnes pagasts, Alūksne");
    setValue("harvestableArea", "3");
    setValue("liquidFellingArea", "2");
    setValue("fellingType", "clear");
    setValue("fellingOwner", "same");
    setValue("measurementStatus", "yes");
    setValue("accessAgreement", "verbal");
    setValue("accessConditions", "good");
    setValue("contractType", "standard");
    setValue("signatureType", "digital");
    setValue("email", "test@test.com");
    setValue("phone", "12 345 6789");
    setValue("extraInfo", "Dont read email");
    setValue("trees", [{ code: "priede", percentage: 40 }]);
    setFormStep(7);
  };

  const handleRandomProperty = () => {
    setValue("propertyType", "property");
    setValue("startingPrice", 1000);
    setValue("propertyName", "Alises");
    setValue("region", "kurzeme");
    setValue("cadastre", "440044");
    setValue("propertyAddress", "Saldus novads, Alūksnes pagasts, Alūksne");
    setValue("propertySize", "3");
    setValue("forestSize", "2");
    setValue("forestStatus", "fully");
    setValue("contractType", "standard");
    setValue("signatureType", "digital");
    setValue("email", "test@test.com");
    setValue("phone", "12 345 6789");
    setValue("extraInfo", "Dont read email");
    setFormStep(7);
  };

  const buildPayload = (values) => {
    const commonData = {
      startingPrice: values.startingPrice,
      endTime: values.endTime ? values.endTime.toISOString() : null,
      propertyName: values.propertyName,
      region: values.region,
      cadastre: values.cadastre,
      propertyAddress: values.propertyAddress,
      coordinates: values.coordinates
        ? values.coordinates
        : { lat: 56.8796, lng: 24.6032 },
      contractType: values.contractType,
      signatureType: values.signatureType,
      email: values.email,
      phone: values.phone,
      extraInfo: values.extraInfo,
      attachments: null,
    };

    switch (values.propertyType) {
      case "property":
        return {
          ...commonData,
          propertyType: "property",
          propertySize: values.propertySize,
          forestStatus: values.forestStatus,
          forestSize: values.forestSize,
        };

      case "felling":
        return {
          ...commonData,
          propertyType: "felling",
          harvestableArea: values.harvestableArea,
          liquidFellingArea: values.liquidFellingArea,
          measurementStatus: values.measurementStatus,
          fellingType: values.fellingType,
          accessAgreement: values.accessAgreement,
          accessConditions: values.accessConditions,
          fellingOwner: values.fellingOwner,
          treesOnProperty: trees,
        };

      case "timber":
        return {
          ...commonData,
          propertyType: "timber",
          timberDescription: values.timberDescription,
        };

      case "wood_chips":
        return {
          ...commonData,
          propertyType: "wood_chips",
          timberDescription: values.timberDescription,
        };

      default:
        return commonData; // fallback
    }
  };

  const verifyPayload = (payload) => {
    const requiredFields = [
      "propertyType",
      "startingPrice",
      "endTime",
      "region",
      "propertyAddress",
      "contractType",
      "signatureType",
      "email",
      "phone",
    ];
    const requiredFieldsForProperty = [
      ...requiredFields,
      "propertyName",
      "cadastre",
      "propertySize",
      "forestSize",
      "forestStatus",
    ];
    const requiredFieldsForFelling = [
      ...requiredFields,
      "propertyName",
      "cadastre",
      "harvestableArea",
      "liquidFellingArea",
      "measurementStatus",
      "fellingType",
      "accessAgreement",
      "accessConditions",
      "fellingOwner",
      "treesOnProperty",
    ];

    let fieldsToCheck = [];

    if (payload.propertyType === "property") {
      fieldsToCheck = requiredFieldsForProperty;
    } else if (payload.propertyType === "felling") {
      fieldsToCheck = requiredFieldsForFelling;
    } else {
      fieldsToCheck = requiredFields;
    }

    const missingFields = fieldsToCheck.filter(
      (field) =>
        payload[field] === undefined ||
        payload[field] === null ||
        payload[field] === ""
    );

    return {
      isValid: missingFields.length === 0,
      missingFields,
    };
  };

  const onSubmit = async (data) => {
    setLoading(true);

    try {
      const payload = buildPayload(values);
      // console.log("Payload:", values);

      // lat
      // :
      // 56.258075245
      // lng
      // :
      // 25.22908512
      // return;

      const payloadStatus = verifyPayload(payload);

      if (!payloadStatus.isValid) {
        showAlert(t("errors.err"), "error");
        return;
      }

      let formData = new FormData();
      formData.append("payload", JSON.stringify(payload));
      attachments.forEach((file) => {
        formData.append("attachments", file);
      });

      const auction = await createAuction(formData);

      if (auction) {
        setBlockNavigation(false);
        showAlert(t("create_auction_page.create_success"));
        await new Promise((resolve) => setTimeout(resolve, 2000));
        navigate("/izsoles");
      }
    } catch (error) {
      showAlert(t("create_auction_page.create_failure"), "error");
      setBlockNavigation(true);
      setErrorMessage(error.message || t("create_auction_page.create_failure"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserStatus();
  }, []);

  return (
    <>
      <Container
        maxWidth="lg"
        sx={{
          display: "flex",
          // justifyContent: 'center',
          flexDirection: "column",
          alignItems: "center",
          minHeight: "80vh", // 80vh height
          bgcolor: "background.paper",
          position: "relative",
          mt: 4,
          mb: 4,
          py: 4,
        }}
      >
        <MUIAlert
          open={alertOpen}
          onClose={handleAlertClose}
          severity={alertSeverity}
          message={alertMessage}
        />
        <Box sx={{ display: { xs: "none", sm: "block" } }}>
          <Stepper
            activeStep={formStep - 1}
            alternativeLabel
            sx={{
              mb: 2,
              maxWidth: {
                xs: "90vw",
              },
            }}
          >
            {steps.map((label, index) => {
              const stepNumber = index + 1;
              return (
                <Step key={label} completed={formStep > stepNumber}>
                  <StepLabel
                    onClick={() => {
                      clearErrors();
                      setFormStep(stepNumber);
                    }}
                    sx={{ cursor: "pointer" }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>
        </Box>

        <FormProvider {...methods}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit(onSubmit)(e);
            }}
            sx={{ maxWidth: { xs: "100vw", md: "600px" } }}
          >
            {/* Auction Data (step1) */}
            {formStep === 1 && (
              <Paper
                sx={{
                  width: "100%",
                  minWidth: { md: "700px" },
                  maxWidth: "700px",
                  p: 3,
                  border: "1px solid lightgray",
                  bgcolor: "#fcfcfc",
                }}
              >
                <Typography
                  variant="h3"
                  sx={{ fontWeight: "bold", mb: 2, textAlign: "left" }}
                >
                  {t("create_auction_page.step_1_title")}
                </Typography>

                {/* Error message display */}
                {errorMessage && (
                  <Typography
                    variant="body2"
                    sx={{ color: "red", mb: 2, textAlign: "center" }}
                  >
                    {errorMessage}
                  </Typography>
                )}

                {/* Property Type */}
                <FormControl size="small" fullWidth margin="normal">
                  <InputLabel>{t("forms.property_type")}</InputLabel>
                  <Select
                    label={t("forms.property_type")}
                    {...register("propertyType")}
                    defaultValue={watch("propertyType")}
                    error={!!errors.propertyType}
                  >
                    <MenuItem value="property">
                      {propertyTypeLabels.property}
                    </MenuItem>
                    <MenuItem value="felling">
                      {propertyTypeLabels.felling}
                    </MenuItem>
                    {/* <MenuItem value="timber">
                      {propertyTypeLabels.timber}
                    </MenuItem>
                    <MenuItem value="wood_chips">
                      {propertyTypeLabels.wood_chips}
                    </MenuItem> */}
                  </Select>
                  <Typography variant="body2" color="error">
                    {errors.propertyType?.message}
                  </Typography>
                </FormControl>

                {/* Starting Price */}
                <TextField
                  label={t("forms.start_price")}
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  size="small"
                  {...register("startingPrice")}
                  error={!!errors.startingPrice}
                  helperText={errors.startingPrice?.message}
                  defaultValue={watch("startingPrice")}
                  type="number"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">€</InputAdornment>
                    ),
                    inputProps: { min: 100 },
                  }}
                />

                {/* End Time */}

                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Controller
                    name="endTime"
                    control={control}
                    rules={{ required: t("errors.end_time_required") }}
                    render={({ field }) => (
                      <DateTimePicker
                        {...field}
                        label={t("forms.end_time")}
                        onChange={(value) => field.onChange(value)}
                        value={field.value}
                        ampm={false} // 24h format
                        views={["year", "month", "day", "hours", "minutes"]}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            margin="normal"
                            size="small"
                            error={!!errors.endTime}
                            helperText={errors.endTime?.message}
                          />
                        )}
                      />
                    )}
                  />
                </LocalizationProvider>

                <NextStepButton />
              </Paper>
            )}

            {/* Location Data (step2) */}
            {formStep === 2 && (
              <Paper
                sx={{
                  width: "100%",
                  minWidth: { md: "700px" },
                  maxWidth: "700px",
                  p: 3,
                  border: "1px solid lightgray",
                  bgcolor: "#fcfcfc",
                }}
              >
                <Typography
                  variant="h3"
                  sx={{ fontWeight: "bold", mb: 2, textAlign: "left" }}
                >
                  {t("create_auction_page.step_2_title")}
                </Typography>

                {/* Error message display */}
                {errorMessage && (
                  <Typography
                    variant="body2"
                    sx={{ color: "red", mb: 2, textAlign: "center" }}
                  >
                    {errorMessage}
                  </Typography>
                )}

                {/* Property Name */}
                <TextField
                  label={t("forms.property_name")}
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  size="small"
                  {...register("propertyName")}
                  error={!!errors.propertyName}
                  helperText={errors.propertyName?.message}
                  defaultValue={watch("propertyName")}
                  type="text"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={t("forms.property_name_tooltip")}>
                          <IconButton edge="end" size="small">
                            <HelpOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Address*/}
                <TextField
                  label={t("forms.property_address")}
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="normal"
                  {...register("propertyAddress")}
                  error={!!errors.propertyAddress}
                  helperText={errors.propertyAddress?.message}
                  defaultValue={watch("propertyAddress")}
                />

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    {/* Region */}
                    <FormControl
                      size="small"
                      fullWidth
                      margin="normal"
                      error={!!errors.region}
                    >
                      <InputLabel>{t("forms.region")}</InputLabel>
                      <Select
                        label={t("forms.region")}
                        {...register("region")}
                        defaultValue={watch("region")}
                      >
                        <MenuItem value="riga">{regionLabels.riga}</MenuItem>
                        <MenuItem value="kurzeme">
                          {regionLabels.kurzeme}
                        </MenuItem>
                        <MenuItem value="vidzeme">
                          {regionLabels.vidzeme}
                        </MenuItem>
                        <MenuItem value="zemgale">
                          {regionLabels.zemgale}
                        </MenuItem>
                        <MenuItem value="latgale">
                          {regionLabels.latgale}
                        </MenuItem>
                      </Select>
                      <FormHelperText color="error">
                        {errors.region?.message}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    {/* cadastre */}
                    <TextField
                      label={t("forms.cadastre")}
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      size="small"
                      {...register("cadastre")}
                      error={!!errors.cadastre}
                      helperText={errors.cadastre?.message}
                      defaultValue={watch("cadastre")}
                      type="text"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title={t("forms.cadastre_tooltip")}>
                              <IconButton edge="end" size="small">
                                <HelpOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>

                {/* TODO set default value so it remembers the location */}
                <LocationPicker setValue={setValue} watch={watch} />

                <NextStepButton />
                <PreviousStepButton />
              </Paper>
            )}

            {/* Special Data (step3) */}
            {formStep === 3 && (
              <Paper
                sx={{
                  width: "100%",
                  minWidth: { md: "700px" },
                  maxWidth: "700px",
                  p: 3,
                  border: "1px solid lightgray",
                  bgcolor: "#fcfcfc",
                }}
              >
                <Typography
                  variant="h3"
                  sx={{ fontWeight: "bold", mb: 2, textAlign: "left" }}
                >
                  {t("create_auction_page.step_3_title")}
                </Typography>

                {/* Error message display */}
                {errorMessage && (
                  <Typography
                    variant="body2"
                    sx={{ color: "red", mb: 2, textAlign: "center" }}
                  >
                    {errorMessage}
                  </Typography>
                )}

                {propertyType === "property" && (
                  <>
                    <TextField
                      label={t("forms.property_size")}
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      size="small"
                      {...register("propertySize")}
                      error={!!errors.propertySize}
                      helperText={errors.propertySize?.message}
                      defaultValue={watch("propertySize")}
                      type="number"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">Ha</InputAdornment>
                        ),
                        inputProps: { min: 0, step: 0.1 },
                      }}
                    />

                    <FormControl size="small" fullWidth margin="normal">
                      <InputLabel>{t("forms.forest_status")}</InputLabel>
                      <Select
                        label={t("forms.forest_status")}
                        {...register("forestStatus")}
                        error={!!errors.forestStatus}
                        defaultValue={watch("forestStatus")}
                      >
                        <MenuItem value="none">
                          {forestStatusLabels.none}
                        </MenuItem>
                        <MenuItem value="unknown">
                          {forestStatusLabels.unknown}
                        </MenuItem>
                        <MenuItem value="partially">
                          {forestStatusLabels.partially}
                        </MenuItem>
                        <MenuItem value="fully">
                          {forestStatusLabels.fully}
                        </MenuItem>
                      </Select>
                      <Typography variant="body2" color="error">
                        {errors.forestStatus?.message}
                      </Typography>
                    </FormControl>
                    {forestStatus !== "none" && (
                      <TextField
                        label={t("forms.forest_size")}
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        size="small"
                        {...register("forestSize")}
                        error={!!errors.forestSize}
                        helperText={errors.forestSize?.message}
                        defaultValue={watch("forestSize")}
                        type="number"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">Ha</InputAdornment>
                          ),
                          inputProps: { min: 0, step: 0.1 },
                        }}
                      />
                    )}
                  </>
                )}

                {propertyType === "felling" && (
                  <>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label={t("forms.harvestable_area")}
                          variant="outlined"
                          fullWidth
                          margin="normal"
                          size="small"
                          {...register("harvestableArea")}
                          error={!!errors.harvestableArea}
                          helperText={errors.harvestableArea?.message}
                          defaultValue={watch("harvestableArea")}
                          type="number"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                Ha
                              </InputAdornment>
                            ),
                            inputProps: { min: 0, step: 0.1 },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label={t("forms.liquid_felling_area")}
                          variant="outlined"
                          fullWidth
                          margin="normal"
                          size="small"
                          {...register("liquidFellingArea")}
                          error={!!errors.liquidFellingArea}
                          helperText={errors.liquidFellingArea?.message}
                          defaultValue={watch("liquidFellingArea")}
                          type="number"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                Ha
                              </InputAdornment>
                            ),
                            inputProps: { min: 0, step: 0.1 },
                          }}
                        />
                      </Grid>
                    </Grid>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <FormControl size="small" fullWidth margin="normal">
                          <InputLabel>
                            {t("forms.measurement_status")}
                          </InputLabel>
                          <Select
                            label={t("forms.measurement_status")}
                            {...register("measurementStatus")}
                            error={!!errors.measurementStatus}
                            defaultValue={watch("measurementStatus")}
                          >
                            <MenuItem value="no">
                              {measurementStatusLabels.no}
                            </MenuItem>
                            <MenuItem value="yes">
                              {measurementStatusLabels.yes}
                            </MenuItem>
                          </Select>
                          <Typography variant="body2" color="error">
                            {errors.measurementStatus?.message}
                          </Typography>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl size="small" fullWidth margin="normal">
                          <InputLabel>{t("forms.felling_type")}</InputLabel>
                          <Select
                            label={t("forms.felling_type")}
                            {...register("fellingType")}
                            error={!!errors.fellingType}
                            defaultValue={watch("fellingType")}
                          >
                            <MenuItem value="clear">
                              {fellingTypeLabels.clear}
                            </MenuItem>
                            <MenuItem value="selection">
                              {fellingTypeLabels.selection}
                            </MenuItem>
                            <MenuItem value="maintenance">
                              {fellingTypeLabels.maintenance}
                            </MenuItem>
                            <MenuItem value="random">
                              {fellingTypeLabels.random}
                            </MenuItem>
                          </Select>
                          <Typography variant="body2" color="error">
                            {errors.fellingType?.message}
                          </Typography>
                        </FormControl>
                      </Grid>
                    </Grid>

                    <FormControl size="small" fullWidth margin="normal">
                      <InputLabel>{t("forms.access_agreement")}</InputLabel>
                      <Select
                        label={t("forms.access_agreement")}
                        {...register("accessAgreement")}
                        error={!!errors.accessAgreement}
                        defaultValue={watch("accessAgreement")}
                      >
                        <MenuItem value="none">
                          {accessAgreementLabels.none}
                        </MenuItem>
                        <MenuItem value="agreed">
                          {accessAgreementLabels.agreed}
                        </MenuItem>
                        <MenuItem value="verbal">
                          {accessAgreementLabels.verbal}
                        </MenuItem>
                        <MenuItem value="not_agreed">
                          {accessAgreementLabels.not_agreed}
                        </MenuItem>
                      </Select>
                      <Typography variant="body2" color="error">
                        {errors.accessAgreement?.message}
                      </Typography>
                    </FormControl>

                    <FormControl size="small" fullWidth margin="normal">
                      <InputLabel>{t("forms.access_conditions")}</InputLabel>
                      <Select
                        label={t("forms.access_conditions")}
                        {...register("accessConditions")}
                        error={!!errors.accessConditions}
                        defaultValue={watch("accessConditions")}
                      >
                        <MenuItem value="none">
                          {accessConditionsLabels.none}
                        </MenuItem>
                        <MenuItem value="bad">
                          {accessConditionsLabels.bad}
                        </MenuItem>
                        <MenuItem value="okay">
                          {accessConditionsLabels.okay}
                        </MenuItem>
                        <MenuItem value="good">
                          {accessConditionsLabels.good}
                        </MenuItem>
                      </Select>
                      <Typography variant="body2" color="error">
                        {errors.accessConditions?.message}
                      </Typography>
                    </FormControl>

                    <FormControl size="small" fullWidth margin="normal">
                      <InputLabel>{t("forms.felling_owner")}</InputLabel>
                      <Select
                        label={t("forms.felling_owner")}
                        {...register("fellingOwner")}
                        error={!!errors.fellingOwner}
                        defaultValue={watch("fellingOwner")}
                      >
                        <MenuItem value="same">
                          {fellingOwnerLabels.same}
                        </MenuItem>
                        <MenuItem value="different">
                          {fellingOwnerLabels.different}
                        </MenuItem>
                      </Select>
                      <Typography variant="body2" color="error">
                        {errors.fellingOwner?.message}
                      </Typography>
                    </FormControl>

                    <Divider sx={{ mt: 3, mb: 4 }} />

                    <TreePicker
                      value={trees || []}
                      onChange={(newTrees) => setValue("trees", newTrees)}
                    />

                    <Divider sx={{ mt: 3, mb: 4 }} />
                  </>
                )}

                {(propertyType === "timber" ||
                  propertyType === "wood_chips") && (
                  // TODO ask what should be here
                  <>
                    <TextField
                      label={t("forms.timber_offer")}
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      size="small"
                      multiline
                      rows={4}
                      {...register("timberDescription")}
                      error={!!errors.timberDescription}
                      helperText={errors.timberDescription?.message}
                      defaultValue={watch("timberDescription")}
                    />
                    <Typography variant="h6">
                      {t("create_auction_page.timber_tip_title")}
                    </Typography>
                    <Typography>
                      {t("create_auction_page.timber_tip_body")}
                    </Typography>
                  </>
                )}

                <NextStepButton />
                <PreviousStepButton />
              </Paper>
            )}

            {/* Contract Data (step4) */}
            {formStep === 4 && (
              <Paper
                sx={{
                  width: "100%",
                  minWidth: { md: "700px" },
                  maxWidth: "700px",
                  p: 3,
                  border: "1px solid lightgray",
                  bgcolor: "#fcfcfc",
                }}
              >
                <Typography
                  variant="h3"
                  sx={{ fontWeight: "bold", mb: 2, textAlign: "left" }}
                >
                  {t("create_auction_page.step_4_title")}
                </Typography>

                {/* Error message display */}
                {errorMessage && (
                  <Typography
                    variant="body2"
                    sx={{ color: "red", mb: 2, textAlign: "center" }}
                  >
                    {errorMessage}
                  </Typography>
                )}

                {/* Contract Type */}
                <FormControl size="small" fullWidth margin="normal">
                  <InputLabel>{t("forms.contract_type")}</InputLabel>
                  <Select
                    label={t("forms.contract_type")}
                    {...register("contractType")}
                    error={!!errors.contractType}
                    defaultValue={watch("contractType")}
                  >
                    <MenuItem value="standard">
                      {contractTypeLabels.standard}
                    </MenuItem>
                    <MenuItem value="attachment">
                      {contractTypeLabels.attachment}
                    </MenuItem>
                  </Select>
                  <Typography variant="body2" color="error">
                    {errors.contractType?.message}
                  </Typography>
                </FormControl>

                {/* Signature Type */}
                <FormControl size="small" fullWidth margin="normal">
                  <InputLabel>{t("forms.signature_type")}</InputLabel>
                  <Select
                    label={t("forms.signature_type")}
                    {...register("signatureType")}
                    error={!!errors.signatureType}
                    defaultValue={watch("signatureType")}
                  >
                    <MenuItem value="digital">
                      {signatureTypeLabels.digital}
                    </MenuItem>
                    <MenuItem value="physical">
                      {signatureTypeLabels.physical}
                    </MenuItem>
                  </Select>
                  <Typography variant="body2" color="error">
                    {errors.signatureType?.message}
                  </Typography>
                </FormControl>

                <NextStepButton />
                <PreviousStepButton />
              </Paper>
            )}

            {/* Extra Information (step5) */}
            {formStep === 5 && (
              <Paper
                sx={{
                  width: "100%",
                  minWidth: { md: "700px" },
                  maxWidth: "700px",
                  p: 3,
                  border: "1px solid lightgray",
                  bgcolor: "#fcfcfc",
                }}
              >
                <Typography
                  variant="h3"
                  sx={{ fontWeight: "bold", mb: 2, textAlign: "left" }}
                >
                  {t("create_auction_page.step_5_title")}
                </Typography>

                {/* Error message display */}
                {errorMessage && (
                  <Typography
                    variant="body2"
                    sx={{ color: "red", mb: 2, textAlign: "center" }}
                  >
                    {errorMessage}
                  </Typography>
                )}

                <TextField
                  label={t("forms.extra_email")}
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  size="small"
                  {...register("email")}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  defaultValue={watch("email")}
                  type="email"
                />

                <TextField
                  label={t("forms.extra_phone")}
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  size="small"
                  {...register("phone")}
                  error={!!errors.phone}
                  helperText={errors.phone?.message}
                  defaultValue={watch("phone")}
                  type="tel"
                />

                {/* TODO replace with rich editor */}
                <TextField
                  label={t("forms.extra_info")}
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  size="small"
                  multiline
                  rows={4}
                  {...register("extraInfo")}
                  error={!!errors.extraInfo}
                  helperText={errors.extraInfo?.message}
                  defaultValue={watch("extraInfo")}
                />

                <NextStepButton />
                <PreviousStepButton />
              </Paper>
            )}

            {/* Attachments Information (step6) */}
            {formStep === 6 && (
              <Paper
                sx={{
                  width: "100%",
                  minWidth: { md: "700px" },
                  maxWidth: "700px",
                  p: 3,
                  border: "1px solid lightgray",
                  bgcolor: "#fcfcfc",
                }}
              >
                <Typography
                  variant="h3"
                  sx={{ fontWeight: "bold", mb: 2, textAlign: "left" }}
                >
                  {t("create_auction_page.step_6_title")}
                </Typography>

                {/* Error message display */}
                {errorMessage && (
                  <Typography
                    variant="body2"
                    sx={{ color: "red", mb: 2, textAlign: "center" }}
                  >
                    {errorMessage}
                  </Typography>
                )}

                {propertyType === "property" && (
                  <Alert
                    variant="outlined"
                    severity="info"
                    sx={{
                      mb: 2,
                      backgroundColor: "info.background",
                    }}
                  >
                    <AlertTitle>
                      {t("create_auction_page.sellers_tip_title")}
                    </AlertTitle>
                    {t("create_auction_page.sellers_tip_body")} <br />
                  </Alert>
                )}
                {propertyType === "felling" && (
                  <Alert
                    variant="outlined"
                    severity="info"
                    sx={{
                      mb: 2,
                      backgroundColor: "info.background",
                    }}
                  >
                    <AlertTitle>
                      {t("create_auction_page.felling_tip_title")}
                    </AlertTitle>
                    {t("create_auction_page.felling_tip_line_1")}
                    <br />
                    {t("create_auction_page.felling_tip_line_2")}
                    <br />
                    {t("create_auction_page.felling_tip_line_3")}
                    <br />
                    {t("create_auction_page.felling_tip_line_4")}
                    <br />
                  </Alert>
                )}
                {(propertyType === "timber" ||
                  propertyType === "wood_chips") && (
                  <Alert
                    variant="outlined"
                    severity="info"
                    sx={{
                      mb: 2,
                      backgroundColor: "info.background",
                    }}
                  >
                    <AlertTitle>
                      {t("create_auction_page.sellers_tip_title")}
                    </AlertTitle>
                    {t("create_auction_page.sellers_tip_body")} <br />
                  </Alert>
                )}

                <FilePicker onFilesChange={setAttachments} />

                <NextStepButton />
                <PreviousStepButton />
              </Paper>
            )}

            {/* Review & Submit (step7) */}
            {formStep === 7 && (
              <Paper
                sx={{
                  width: "100%",
                  minWidth: { md: "700px" },
                  maxWidth: "700px",
                  p: 3,
                  border: "1px solid lightgray",
                  bgcolor: "#fcfcfc",
                }}
              >
                <Typography
                  variant="h3"
                  sx={{ fontWeight: "bold", mb: 2, textAlign: "left" }}
                >
                  {t("create_auction_page.step_7_title")}
                </Typography>

                {errorMessage && (
                  <Typography
                    variant="body2"
                    sx={{ color: "red", mb: 2, textAlign: "center" }}
                  >
                    {errorMessage}
                  </Typography>
                )}

                {(() => {
                  const values = watch();

                  return (
                    <>
                      <Typography variant="subtitle1">
                        <b>{t("forms.property_type")}:</b>{" "}
                        {propertyTypeLabels[values.propertyType]}
                      </Typography>
                      <Typography variant="subtitle1">
                        <b>{t("forms.start_price")}:</b> {values.startingPrice}
                      </Typography>
                      <Typography variant="subtitle1">
                        <b>{t("forms.end_time")}:</b>{" "}
                        {values.endTime.toLocaleString()}
                      </Typography>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle1">
                        <b>{t("forms.property_name")}:</b> {values.propertyName}
                      </Typography>
                      <Typography variant="subtitle1">
                        <b>{t("forms.region")}:</b>{" "}
                        {regionLabels[values.region]}
                      </Typography>
                      <Typography variant="subtitle1">
                        <b>{t("forms.cadastre")}:</b> {values.cadastre}
                      </Typography>
                      <Typography variant="subtitle1">
                        <b>{t("forms.property_address")}:</b>{" "}
                        {values.propertyAddress}
                      </Typography>
                      <Divider sx={{ my: 2 }} />
                      {propertyType === "property" && (
                        <>
                          <Typography variant="subtitle1">
                            <b>{t("forms.property_size")}:</b>{" "}
                            {values.propertySize} ha
                          </Typography>
                          <Typography variant="subtitle1">
                            <b>{t("forms.forest_status")}:</b>{" "}
                            {forestStatusLabels[values.forestStatus]}
                          </Typography>
                          {values.forestStatus !== "none" && (
                            <Typography variant="subtitle1">
                              <b>{t("forms.forest_size")}:</b>{" "}
                              {values.forestSize} ha
                            </Typography>
                          )}
                        </>
                      )}
                      {propertyType === "felling" && (
                        <>
                          <Typography variant="subtitle1">
                            <b>{t("forms.harvestable_area")}:</b>{" "}
                            {values.harvestableArea} ha
                          </Typography>
                          <Typography variant="subtitle1">
                            <b>{t("forms.liquid_felling_area")}:</b>{" "}
                            {values.liquidFellingArea} ha
                          </Typography>
                          <Typography variant="subtitle1">
                            <b>{t("forms.measurement_status")}:</b>{" "}
                            {measurementStatusLabels[values.measurementStatus]}
                          </Typography>
                          <Typography variant="subtitle1">
                            <b>{t("forms.felling_type")}:</b>{" "}
                            {fellingTypeLabels[values.fellingType]}
                          </Typography>
                          <Typography variant="subtitle1">
                            <b>{t("forms.access_agreement")}:</b>{" "}
                            {accessAgreementLabels[values.accessAgreement]}
                          </Typography>
                          <Typography variant="subtitle1">
                            <b>{t("forms.access_conditions")}:</b>{" "}
                            {accessConditionsLabels[values.accessConditions]}
                          </Typography>
                          <Typography variant="subtitle1">
                            <b>{t("forms.felling_owner")}:</b>{" "}
                            {fellingOwnerLabels[values.fellingOwner]}
                          </Typography>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="subtitle1" sx={{ mt: 2 }}>
                            <b>{t("forms.tree_types")}:</b>
                          </Typography>
                          {values.trees && values.trees.length > 0 ? (
                            values.trees.map(({ code, percentage }, i) => {
                              const labelObj = treeTypeLabels.find(
                                (t) => t.code === code
                              );
                              const label = labelObj
                                ? labelObj.label
                                : code || "(nav izvēlēts)";
                              return (
                                <Typography
                                  key={i}
                                  variant="body2"
                                  sx={{ ml: 2 }}
                                >
                                  {label}: {percentage}%
                                </Typography>
                              );
                            })
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{ ml: 2, fontStyle: "italic" }}
                            >
                              {t("create_auction_page.no_trees_selected")}
                            </Typography>
                          )}
                        </>
                      )}
                      {(propertyType === "timber" ||
                        propertyType === "wood_chips") && (
                        <Typography variant="subtitle1">
                          <b>{t("forms.timber_offer")}:</b>{" "}
                          {values.timberDescription}
                        </Typography>
                      )}
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle1">
                        <b>{t("forms.contract_type")}:</b>{" "}
                        {contractTypeLabels[values.contractType]}
                      </Typography>
                      <Typography variant="subtitle1">
                        <b>{t("forms.signature_type")}:</b>{" "}
                        {signatureTypeLabels[values.signatureType]}
                      </Typography>

                      {/* Add more fields below similarly */}
                    </>
                  );
                })()}
              </Paper>
            )}

            {/* ======================= SUBMIT FORM ======================= */}

            {formStep === 7 && (
              <>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  color="secondary"
                  size="large"
                  sx={{
                    mt: 2,
                    py: 1,
                    // px: 20,
                    // mx: 'auto',
                    fontWeight: "bold",
                    display: "flex", // Use flexbox for centering
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "50px",
                    py: 1.4,
                    // STRIPE SHADOW
                    // boxShadow: '0px 0px 0px rgba(0,0,0,0.0)',
                    "&:hover": {
                      backgroundColor: "secondary.light",
                      // boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.08), 0px 1px 3px rgba(0, 0, 0, 0.05)',
                    },
                  }}
                  disabled={loading} // Disable the button when loading
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" /> // Show spinner when loading
                  ) : (
                    t("general.continue")
                  )}
                </Button>
                <PreviousStepButton />
              </>
            )}

            {process.env.APP_ENV != "production" && formStep === 1 && (
              <>
                <Button
                  variant="contained"
                  fullWidth
                  color="primary"
                  sx={{
                    mt: 2,
                    py: 1,
                    fontWeight: "bold",
                    display: "flex", // Use flexbox for centering
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onClick={() => handleRandomProperty()}
                >
                  Random property
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  color="primary"
                  sx={{
                    mt: 2,
                    py: 1,
                    fontWeight: "bold",
                    display: "flex", // Use flexbox for centering
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onClick={() => handleRandomFelling()}
                >
                  Random felling
                </Button>
              </>
            )}
          </form>
        </FormProvider>
      </Container>
    </>
  );
};

export default CreateAuction;
